import { TestType } from '@/models/SharedTypes';
import { getCategoryDisplayName } from '@/utils/dashboard/categoryUtils';
import { AREAS as AREAS_2026 } from '@/data/diagnosticBank';
import type { RetakeReport, RetakeBlueprint } from '@/models/UserTypes';

export type { RetakeReport, RetakeBlueprint };

/**
 * The four legacy (pre-Aug-2026) ASWB content-area codes.
 *
 * These stay the WIRE vocabulary even for a 2026 score report. The merged
 * backend contract (aswb-backend#296, contracts/onboarding.ts RetakeReportSchema)
 * types `lowestAreas` as a strict `enum(['I','II','III','IV'])` and REJECTS any
 * other code, and its matcher (`blueprintAreaForCategory`) folds both blueprints
 * onto these four. So the UI shows the era's real area names and we send the code.
 */
export const CONTENT_AREA_CODES = ['I', 'II', 'III', 'IV'] as const;
export type ContentAreaCode = (typeof CONTENT_AREA_CODES)[number];

/** Attempt buckets offered in the UI; 3 is rendered as "3+". Contract requires >= 1. */
export const ATTEMPT_OPTIONS: readonly number[] = [1, 2, 3];

/** ASWB consolidated 4 content areas into 3 for exams on/after this date. */
export const BLUEPRINT_CHANGE_LABEL = 'August 3, 2026';

export interface ContentArea {
  code: ContentAreaCode;
  label: string;
}

/**
 * The 2026 blueprint's THREE areas, in the order the app's own 2026 bank lists
 * them, each carrying the legacy code the backend matcher folds it onto.
 *
 * The mapping is not a guess: it is exactly what the backend's `keywordArea`
 * (adaptive-core/examBlueprint.ts) does. value/ethics -> IV, assess/plan -> II,
 * interven/practice -> III.
 *
 * NOTE, because it is easy to get backwards: the area with NO 2026 counterpart
 * is I (Human Development & Behavior), which was absorbed into the other three.
 * IV (Values & Ethics) not only survives, it becomes the LARGEST domain in 2026.
 * So IV appears in BOTH lists and I is the pre-August-only one.
 */
const AREAS_2026_WITH_CODES: ContentArea[] = [
  { code: 'IV', label: AREAS_2026[0] }, // Values & Ethics
  { code: 'II', label: AREAS_2026[1] }, // Assessment & Planning
  { code: 'III', label: AREAS_2026[2] }, // Intervention & Practice
];

/**
 * The content areas a candidate's score report actually listed, given when they
 * sat for the exam.
 *
 * Labels for the legacy era come from the app's existing code -> label table
 * (utils/dashboard/categoryUtils), the same one the dashboard already shows.
 *
 * `exam` is accepted (and unused today) on purpose: ASWB's area TITLES differ
 * slightly per exam and the backend's getAvailableCategories returns exam-scoped
 * labels. When that lands, only this function changes. The CODES are exam-stable,
 * which is why the wire value is the code, not the label.
 */
export function contentAreasForExam(_exam: TestType, blueprint: RetakeBlueprint): ContentArea[] {
  if (blueprint === 'aug2026') return AREAS_2026_WITH_CODES;
  return CONTENT_AREA_CODES.map((code) => ({ code, label: getCategoryDisplayName(code) }));
}

/** The in-progress answers the step holds. `hasRetaken: false` is the default. */
export interface RetakeAnswers {
  hasRetaken: boolean;
  /** Which blueprint their score report used. Defaults to the current one. */
  blueprint: RetakeBlueprint;
  attempts: number;
  lowestAreas: string[];
  nearMiss: boolean;
}

export const EMPTY_RETAKE_ANSWERS: RetakeAnswers = {
  hasRetaken: false,
  blueprint: 'aug2026',
  attempts: 1,
  lowestAreas: [],
  nearMiss: false,
};

/** Rebuild answers from a saved report so the edit flow opens on what was said. */
export function answersFromReport(report: RetakeReport | undefined | null): RetakeAnswers {
  if (!report) return EMPTY_RETAKE_ANSWERS;
  const attempts = ATTEMPT_OPTIONS.includes(report.attempts)
    ? report.attempts
    : report.attempts > 3
      ? 3
      : 1;
  return {
    hasRetaken: true,
    blueprint: report.blueprint ?? 'aug2026',
    attempts,
    lowestAreas: [...(report.lowestAreas ?? [])],
    nearMiss: !!report.nearMiss,
  };
}

/**
 * Answers -> the exact backend shape, or null when there is nothing to write.
 *
 * Skipping the step (or answering "No") returns null, and null must NOT be sent:
 * a skipped step writes nothing at all. Areas are clamped to the codes valid for
 * the chosen ERA (a 2026 score report can never report area I), deduped, and
 * ordered canonically so the payload is stable.
 *
 * `blueprint` rides along for the backend field that is landing now. Today
 * RetakeReportSchema is a plain z.object, so zod STRIPS the key rather than
 * rejecting the request: sending it is safe and becomes meaningful the moment
 * the backend adds it. Nothing on the web reads it back, so it is not silently
 * load-bearing here.
 */
export function buildRetakeReport(answers: RetakeAnswers, exam: TestType): RetakeReport | null {
  if (!answers.hasRetaken) return null;

  const valid = new Set(contentAreasForExam(exam, answers.blueprint).map((a) => a.code as string));
  const lowestAreas = (CONTENT_AREA_CODES as readonly string[]).filter(
    (code) => valid.has(code) && answers.lowestAreas.includes(code)
  );

  const attempts = ATTEMPT_OPTIONS.includes(answers.attempts) ? answers.attempts : 1;

  return {
    attempts,
    lowestAreas: [...lowestAreas],
    nearMiss: !!answers.nearMiss,
    blueprint: answers.blueprint,
  };
}
